import { BaseThemeType, Button, Link, styled, useMediaQuery } from 'opensolar-ui';
import { FC } from 'react';
import { useTranslate } from 'react-admin';

type NavbarCtaProps = {
  label: string
  link: string
  newTab?: boolean
  onClick?: () => void
}

interface PropTypes {
  breadcrumbs: { label: string; href?: string }[]
  links: { label: string; onClick: () => void }[]
  cta?: NavbarCtaProps
  secondaryCta?: NavbarCtaProps
}

const NavWrapper = styled('div', { name: 'NavWrapper' })(({ theme }) => ({
  gap: 15,
  left: 0,
  color: theme.palette.grey[600],
  width: '100%',
  fontWeight: 450,
  padding: '15px',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
}))

const BreadcrumbsAndLinksWrapper = styled('div', { name: 'BreadcrumbsAndLinksWrapper' })({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '10px',
})

const CtaGroup = styled('div', { name: 'CtaGroup' })({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexShrink: 0,
})

const NavButton = styled(Button, { name: 'NavButton' })(({ theme }) => ({
  color: theme.palette.grey[600],
  fontWeight: 450,
}))

const ExpoLink = styled(Link, { name: 'ExpoLink' })(({ theme }) => ({
  color: theme.palette.grey[600] + '!important',
  textDecoration: 'underline',
}))

const Breadcrumbs = styled('div', { name: 'Breadcrumbs' })({
  display: 'flex',
  gap: 5,
  marginRight: '15px',
})

const Navbar: FC<PropTypes> = ({ breadcrumbs, cta, secondaryCta, links }) => {
  const isTablet = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('md'))
  const isBelowLg = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('lg'))
  const translate = useTranslate()
  const showSecondaryCta = Boolean(secondaryCta && !isBelowLg)
  return (
    <NavWrapper>
      <BreadcrumbsAndLinksWrapper>
        {!!breadcrumbs?.length && (
          <Breadcrumbs>
            {breadcrumbs.map((x, i) => (
              <span key={'breadcrumb-' + i}>
                {x.href ? <ExpoLink href={x.href}>{x.label}</ExpoLink> : <>{x.label}</>}
                {i === breadcrumbs.length - 1 ? '' : ' / '}
              </span>
            ))}
          </Breadcrumbs>
        )}
        {!!links?.length &&
          !isTablet &&
          links.map((link, i) => (
            <NavButton size="small" onClick={link.onClick} key={'navBtn-' + i}>
              {translate(link.label)}
            </NavButton>
          ))}
      </BreadcrumbsAndLinksWrapper>
      {(cta && cta.label) || (secondaryCta && secondaryCta.label && showSecondaryCta) ? (
        <CtaGroup>
          {cta && cta.label && (
            <Link href={cta.link} target={cta.newTab ? '_blank' : undefined} rel="noopener noreferrer">
              <Button variant="contained" color="secondary" size="small" onClick={cta.onClick}>
                {cta.label}
              </Button>
            </Link>
          )}
          {showSecondaryCta && secondaryCta && secondaryCta.label && (
            <Link href={secondaryCta.link} target={secondaryCta.newTab ? '_blank' : undefined} rel="noopener noreferrer">
              <Button variant="outlined" color="secondary" size="small" onClick={secondaryCta.onClick}>
                {secondaryCta.label}
              </Button>
            </Link>
          )}
        </CtaGroup>
      ) : null}
    </NavWrapper>
  )
}

export default Navbar
